import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import { ApiError } from "../api/client.js";
import type { AppointmentDetail } from "../api/types.js";
import { Card } from "../components/Card.js";
import {
  StatusBadge,
  ModalityBadge,
  PatientTypeBadge,
} from "../components/StatusBadge.js";
import { Badge } from "../components/Badge.js";
import { DocumentStatusBadge } from "../components/DocumentStatusBadge.js";
import { CopyButton } from "../components/CopyButton.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { formatDateTime, formatDob, formatRelative } from "../lib/format.js";
import { formatPhone } from "../lib/format-phone.js";
import { staffTeamsChannelName } from "../lib/teams-staff-channel.js";

type Props = { api: AdminApi };

export function AppointmentDetailPage({ api }: Props) {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const q = useQuery({
    enabled: !!id,
    queryKey: ["admin", "appointment", id],
    queryFn: () => api.getAppointment(id as string),
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      api.cancelAppointment(id as string, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "appointment", id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin", "appointments"],
      });
    },
  });

  const retriggerTeamsMutation = useMutation({
    mutationFn: () => api.retriggerTeamsNotification(id as string),
  });

  const resendEmailMutation = useMutation({
    mutationFn: () => api.resendPatientEmail(id as string),
  });

  const requestDocumentsMutation = useMutation({
    mutationFn: () => api.requestDocuments(id as string),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["admin", "appointment", id],
      });
    },
  });

  async function openDocument(docId: string): Promise<void> {
    const { url } = await api.getDocumentViewUrl(docId);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function downloadDocument(docId: string): Promise<void> {
    const { url, filename } = await api.getDocumentDownloadUrl(docId);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  if (q.isPending) {
    return <p className="text-sm text-slate-600">Loading…</p>;
  }
  if (q.isError || !q.data) {
    return <ErrorBanner message="Could not load appointment." />;
  }

  const appt = q.data;

  return (
    <div className="grid gap-4">
      <HeaderStrip
        appt={appt}
        onCancel={(reason) => cancelMutation.mutate(reason)}
        cancelling={cancelMutation.isPending}
        onRetriggerTeams={() => retriggerTeamsMutation.mutate()}
        retriggeringTeams={retriggerTeamsMutation.isPending}
        onResendEmail={() => resendEmailMutation.mutate()}
        resendingEmail={resendEmailMutation.isPending}
      />
      {cancelMutation.isError ? (
        <ErrorBanner message="Could not cancel appointment." />
      ) : null}
      {retriggerTeamsMutation.isError ? (
        <ErrorBanner
          message={retriggerTeamsErrorMessage(retriggerTeamsMutation.error)}
        />
      ) : null}
      {retriggerTeamsMutation.isSuccess ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          Teams notification sent.
        </p>
      ) : null}
      {resendEmailMutation.isError ? (
        <ErrorBanner
          message={resendEmailErrorMessage(resendEmailMutation.error)}
        />
      ) : null}
      {resendEmailMutation.isSuccess ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          Patient email sent.
        </p>
      ) : null}
      {requestDocumentsMutation.isError ? (
        <ErrorBanner
          message={requestDocumentsErrorMessage(requestDocumentsMutation.error)}
        />
      ) : null}
      {requestDocumentsMutation.isSuccess ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          Documents request sent.
        </p>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <PatientCard appt={appt} />
        <VisitCard appt={appt} />
      </div>
      {appt.status === "cancelled" ? (
        <CancellationCard appt={appt} />
      ) : null}
      <DocumentsCard
        appt={appt}
        onOpen={openDocument}
        onDownload={downloadDocument}
        onRequestDocuments={() => requestDocumentsMutation.mutate()}
        requestingDocuments={requestDocumentsMutation.isPending}
      />
    </div>
  );
}

function HeaderStrip({
  appt,
  onCancel,
  cancelling,
  onRetriggerTeams,
  retriggeringTeams,
  onResendEmail,
  resendingEmail,
}: {
  appt: AppointmentDetail;
  onCancel: (reason: string) => void;
  cancelling: boolean;
  onRetriggerTeams: () => void;
  retriggeringTeams: boolean;
  onResendEmail: () => void;
  resendingEmail: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const isCancelled = appt.status === "cancelled";

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xl font-semibold uppercase">
              {appt.public_code}
            </span>
            <CopyButton value={appt.public_code} label="public code" />
            <StatusBadge status={appt.status} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2">
              {formatDateTime(appt.scheduled_start)}
              <CopyButton
                value={formatDateTime(appt.scheduled_start)}
                label="scheduled start"
              />
            </span>
            <span aria-hidden="true">→</span>
            <span className="inline-flex items-center gap-2">
              {formatDateTime(appt.scheduled_end)}
              <CopyButton
                value={formatDateTime(appt.scheduled_end)}
                label="scheduled end"
              />
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            disabled={resendingEmail}
            onClick={onResendEmail}
            className="rounded-md border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {resendingEmail ? "Sending email…" : "Resend patient email"}
          </button>
          <button
            type="button"
            disabled={retriggeringTeams}
            onClick={onRetriggerTeams}
            className="rounded-md border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {retriggeringTeams ? "Sending to Teams…" : "Retrigger Teams notification"}
          </button>
          {!isCancelled ? (
            <>
            {confirming ? (
              <div className="flex w-80 flex-col gap-2">
                <textarea
                  className="field-input min-h-[64px]"
                  placeholder="Reason (required)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setConfirming(false);
                      setReason("");
                    }}
                    className="rounded-md px-3 py-1 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    Keep it
                  </button>
                  <button
                    type="button"
                    disabled={cancelling || !reason.trim()}
                    onClick={() => onCancel(reason.trim())}
                    className="rounded-md bg-rose-600 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {cancelling ? "Cancelling…" : "Confirm cancel"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="rounded-md border border-rose-300 px-3 py-1 text-sm font-medium text-rose-700 hover:bg-rose-50"
              >
                Cancel appointment
              </button>
            )}
            </>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function PatientCard({ appt }: { appt: AppointmentDetail }) {
  const addressText = [
    appt.patient_address_street1,
    appt.patient_address_street2,
    `${appt.patient_address_city}, ${appt.patient_address_state} ${appt.patient_address_zip}`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Card title="Patient">
      <dl className="grid gap-2 text-sm">
        <Row label="First name" copyValue={appt.patient_first_name}>
          {appt.patient_first_name}
        </Row>
        <Row label="Last name" copyValue={appt.patient_last_name}>
          {appt.patient_last_name}
        </Row>
        <Row label="DOB" copyValue={formatDob(appt.patient_dob)}>
          {formatDob(appt.patient_dob)}
        </Row>
        <Row
          label="Gender"
          copyValue={genderLabel(appt.patient_gender)}
        >
          {genderLabel(appt.patient_gender)}
        </Row>
        <Row label="Phone" copyValue={formatPhone(appt.patient_phone)}>
          {formatPhone(appt.patient_phone)}
        </Row>
        <Row label="Email" copyValue={appt.patient_email}>
          {appt.patient_email}
        </Row>
        <Row label="Address" copyValue={addressText}>
          <span className="whitespace-pre-line">{addressText}</span>
        </Row>
      </dl>
    </Card>
  );
}

function VisitCard({ appt }: { appt: AppointmentDetail }) {
  return (
    <Card title="Visit">
      <dl className="grid gap-2 text-sm">
        <Row label="Visit type" copyValue={appt.visit_type_name}>
          {appt.visit_type_name}
        </Row>
        <Row label="Slug" copyValue={appt.visit_type_slug}>
          <code className="text-xs">{appt.visit_type_slug}</code>
        </Row>
        <Row label="Modality" copyValue={appt.modality}>
          <ModalityBadge value={appt.modality} />
        </Row>
        <Row label="Patient type" copyValue={appt.patient_type}>
          <PatientTypeBadge value={appt.patient_type} />
        </Row>
        <Row
          label="Google event"
          {...(appt.gcal_event_id ? { copyValue: appt.gcal_event_id } : {})}
        >
          {appt.gcal_event_id ? (
            <a
              href={`https://calendar.google.com/calendar/event?eid=${encodeURIComponent(
                appt.gcal_event_id,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 hover:underline"
            >
              Open in Google Calendar
            </a>
          ) : (
            <span className="text-slate-500">Not synced</span>
          )}
        </Row>
      </dl>
    </Card>
  );
}

function CancellationCard({ appt }: { appt: AppointmentDetail }) {
  const when = appt.cancelled_at ? formatDateTime(appt.cancelled_at) : null;
  return (
    <Card title="Cancellation">
      <dl className="grid gap-2 text-sm">
        <Row label="When" {...(when ? { copyValue: when } : {})}>
          {when ?? "—"}
        </Row>
        <Row
          label="By"
          {...(appt.cancelled_by ? { copyValue: appt.cancelled_by } : {})}
        >
          {appt.cancelled_by ?? "—"}
        </Row>
        <Row
          label="Reason"
          {...(appt.cancel_reason ? { copyValue: appt.cancel_reason } : {})}
        >
          {appt.cancel_reason ? (
            <span className="italic">"{appt.cancel_reason}"</span>
          ) : (
            "—"
          )}
        </Row>
      </dl>
    </Card>
  );
}

function DocumentsCard({
  appt,
  onOpen,
  onDownload,
  onRequestDocuments,
  requestingDocuments,
}: {
  appt: AppointmentDetail;
  onOpen: (id: string) => void;
  onDownload: (id: string) => void;
  onRequestDocuments: () => void;
  requestingDocuments: boolean;
}) {
  const cancelled = appt.status === "cancelled";
  const complete = appt.document_status === "complete";
  const past = Date.now() > new Date(appt.scheduled_start).getTime() + 24 * 3600_000;
  const disabledReason = cancelled
    ? "This appointment is cancelled."
    : past
      ? "Visit has passed"
      : complete
      ? "All required documents are already on file."
      : undefined;
  const badge = (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <PaymentDocsBadge appt={appt} />
        <button
          type="button"
          disabled={cancelled || past || complete || requestingDocuments}
          title={disabledReason}
          onClick={onRequestDocuments}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {requestingDocuments ? "Sending…" : "Request documents"}
        </button>
      </div>
      {appt.documents_requested_at ? (
        <span className="text-xs text-slate-500">
          Last requested {formatRelative(appt.documents_requested_at)}
        </span>
      ) : null}
    </div>
  );
  if (appt.documents.length === 0) {
    return (
      <Card title="Documents" action={badge}>
        <p className="text-sm text-slate-600">No documents uploaded.</p>
      </Card>
    );
  }
  return (
    <Card title="Documents" action={badge}>
      <ul className="grid gap-2">
        {appt.documents.map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3 text-sm"
          >
            <div className="flex items-center gap-3">
              <Badge tone="slate">{d.kind}</Badge>
              <span className="text-slate-600">
                {d.content_type} · {(d.size_bytes / 1024).toFixed(1)} KB
              </span>
            </div>
            {d.purged_at ? (
              <span className="text-slate-500">
                Purged {formatDateTime(d.purged_at)}
              </span>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpen(d.id)}
                  className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
                >
                  View
                </button>
                <button
                  type="button"
                  onClick={() => onDownload(d.id)}
                  className="rounded-md border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Download
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

const PAYMENT_LABEL = { insurance: "Insurance", self_pay: "Self-pay" } as const;
const REQUIRED_TOTAL = { insurance: 4, self_pay: 2 } as const;
const REQUIRED_KINDS = {
  insurance: [
    "drivers_license_front",
    "drivers_license_back",
    "insurance_front",
    "insurance_back",
  ],
  self_pay: ["drivers_license_front", "drivers_license_back"],
} as const;

function PaymentDocsBadge({ appt }: { appt: AppointmentDetail }) {
  const kinds = new Set(
    appt.documents.filter((d) => d.purged_at === null).map((d) => d.kind),
  );
  const method = appt.payment_method;
  const text = method
    ? `${PAYMENT_LABEL[method]} · ${
        REQUIRED_KINDS[method].filter((k) => kinds.has(k)).length
      }/${REQUIRED_TOTAL[method]}`
    : `Not stated · ${kinds.size}`;
  return (
    <DocumentStatusBadge status={appt.document_status}>{text}</DocumentStatusBadge>
  );
}

function Row({
  label,
  children,
  copyValue,
}: {
  label: string;
  children: React.ReactNode;
  copyValue?: string;
}) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="flex items-start gap-2 text-slate-800">
        <span className="min-w-0 flex-1">{children}</span>
        {copyValue ? <CopyButton value={copyValue} label={label} /> : null}
      </dd>
    </div>
  );
}

function genderLabel(g: AppointmentDetail["patient_gender"]): string {
  switch (g) {
    case "male":
      return "Male";
    case "female":
      return "Female";
    case "nonbinary":
      return "Non-binary";
    case "prefer_not_to_say":
      return "Prefer not to say";
  }
}

function retriggerTeamsErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "no_teams_conversation":
        return (
          `Teams bot is not registered for a channel yet. In the ${staffTeamsChannelName()} team, ` +
          "remove and re-add the Book app to the staff channel, then check " +
          "Settings → Health. The Azure Bot test button does not register a channel."
        );
      case "teams_not_configured":
        return "Teams bot credentials are not configured on the API. Contact IT.";
      case "not_found":
        return "Appointment not found.";
      default:
        return error.message || "Could not send Teams notification.";
    }
  }
  return "Could not send Teams notification.";
}

function resendEmailErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "email_not_configured":
        return "Email is not configured on the API. Contact IT.";
      case "email_send_failed":
        return error.message || "Email provider rejected the send.";
      case "not_found":
        return "Appointment not found.";
      default:
        return error.message || "Could not send patient email.";
    }
  }
  return "Could not send patient email.";
}

const REQUEST_DOCUMENTS_ERRORS: Record<string, string> = {
  appointment_past: "This visit has already happened.",
  documents_complete: "Documents are already complete.",
  appointment_cancelled: "This appointment was cancelled.",
  email_failed: "The email could not be sent. Try again.",
};

function requestDocumentsErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const known = REQUEST_DOCUMENTS_ERRORS[error.code];
    if (known) return known;
  }
  return "Could not request documents.";
}
